import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";

import type { DemoTools } from "./demo";
import type { SessionState, SessionStore } from "./session-store";
import type { IdentityApi, StaffApi } from "./types";

/** Identity services for pages: the identity API, the tab's session, and in the mock the staff API and demo tools. */
export interface IdentityServices {
  identity: IdentityApi;
  session: SessionStore;
  /** Present only where the staff console is reachable (the mock); production runs it on a separate host. */
  staff: StaffApi | undefined;
  /** Present only in the mock (mock AuthKit, demo inbox, "end session now"). */
  demo: DemoTools | undefined;
}

const IdentityContext = createContext<IdentityServices | null>(null);

export function IdentityProvider({
  services,
  children,
}: {
  services: IdentityServices;
  children: ReactNode;
}) {
  return <IdentityContext.Provider value={services}>{children}</IdentityContext.Provider>;
}

function useServices(): IdentityServices {
  const services = useContext(IdentityContext);
  if (services === null) throw new Error("Identity hooks need <IdentityProvider> above them.");
  return services;
}

export function useIdentity(): IdentityApi {
  return useServices().identity;
}

export function useSessionStore(): SessionStore {
  return useServices().session;
}

export function useStaff(): StaffApi | undefined {
  return useServices().staff;
}

export function useDemo(): DemoTools | undefined {
  return useServices().demo;
}

const NO_SUBSCRIPTION = (): (() => void) => () => undefined;
const NO_SESSION = (): undefined => undefined;

/**
 * The tab's session state where an identity provider may be absent (a dialog on its own in a test): `undefined` there,
 * otherwise the same as `useSession`.
 */
export function useOptionalSession(): SessionState | undefined {
  const services = useContext(IdentityContext);
  return useSyncExternalStore(
    services?.session.subscribe ?? NO_SUBSCRIPTION,
    services?.session.getSnapshot ?? NO_SESSION,
  );
}

/** The tab's session state; re-renders when it changes. */
export function useSession(): SessionState {
  const store = useSessionStore();
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}
